import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import media from "../../../media";
import { GlobalStateContext } from "../../../utils/GlobalStateContext";

const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setIsActive] = useState("dashboard");

  const segments = location.pathname.split("/").filter(Boolean);
  const base = segments[0];
  const { globalState, setGlobalState } = useContext(GlobalStateContext);

  /* =========================================================
   🔥 GLOBAL TABLE SELECTION RESET (SINGLE SOURCE OF TRUTH)
   ========================================================= */
  useEffect(() => {
    if (
      globalState?.selectedRowKeys?.length ||
      globalState?.selectedRowKeysChildInfo?.length
    ) {
      setGlobalState((prev) => ({
        ...prev,
        selectedRowKeys: [],
        selectedRowKeysChildInfo: [],
      }));
    }
  }, [location.pathname]);

  const child = segments[1];
  const [isFeedbackRequestsActive, setIsFeedbackRequestsActive] =
    useState(false);

  const [isEscalationsActive, setIsEscalationsActive] = useState(false);

  const role = secureLocalStorage.getItem("role");

  const handleFeedbackRequestsClick = () => {
    setIsFeedbackRequestsActive((prev) => !prev); // toggle open/close
  };

  const handleClientEscalationClick = () => {
    setIsEscalationsActive((prev) => !prev); // TOGGLE
  };

  const handleClick = (val) => {
    setIsActive((prev) => (prev === val ? "" : val));
  };

  const handleSubMenuClick = (val, path) => {
    const cleanPath = path?.replace("/", "");
    const lastPageSearchIn = secureLocalStorage.getItem("lastPageSearchIn");
    if (cleanPath && lastPageSearchIn && cleanPath !== lastPageSearchIn) {
      const searchInput = document.querySelector(".inputS");
      if (searchInput) searchInput.value = "";

      secureLocalStorage.removeItem("hasSearchedValue");
      secureLocalStorage.removeItem("searchKeyword");
    }
  };

  useEffect(() => {
    // RESET submenu states first
    setIsFeedbackRequestsActive(false);
    setIsEscalationsActive(false);

    // DASHBOARD
    if (!base || base === "dashboard") {
      setIsActive("dashboard");
      return;
    }

    // QMS (list + detail pages)
    if (
      [
        "auditForms",
        "audits",
        "deleted-audits",
        "processes",
        "clients",
        "departments",
        "escalations",
      ].includes(base)
    ) {
      setIsActive("qms");

      if (base === "escalations") {
        setIsEscalationsActive(true);
      }
      return;
    }

    // VOC
    if (["feedback-requests", "forms"].includes(base)) {
      setIsActive("voc");

      if (base === "feedback-requests") {
        setIsFeedbackRequestsActive(true);
      }
      return;
    }

    // MANAGE USERS
    if (["users", "roles", "permissions", "sections"].includes(base)) {
      setIsActive("mpsru");
      return;
    }

    // SETTINGS
    if (base === "settings") {
      setIsActive("settings");
    }
  }, [base]);

  const qmsLinks = {
    admin: [
      { path: "/auditForms", label: "Audit Forms", icon: "fa fa-wpforms" },
      { path: "/audits", label: "Audits", icon: "Audits.png" },
      { path: "/deleted-audits", label: "Deleted Audits", icon: "Audits.png" },
      // { path: "/escalations", label: "Escalations", icon: "escalations.png" },
      {
        path: "",
        label: "Escalations",
        icon: "escalations.png",
        subLinks: [
          {
            path: "/escalations/client-escalation",
            label: "Client Escalation",
            icon: "escalations.png",
          },
          {
            path: "/escalations/internal-error-rca",
            label: "Internal Error RCA",
            icon: "escalations.png",
          },
        ],
      },
      { path: "/processes", label: "Processes", icon: "fa fa-refresh" },
      { path: "/clients", label: "Clients", icon: "fa fa-user-secret" },
      { path: "/departments", label: "Departments", icon: "fa fa-building-o" },
    ],
    agent: [{ path: "/audits", label: "Audits", icon: "Audits.png" }],
    atl: [{ path: "/audits", label: "Audits", icon: "Audits.png" }],
    auditor: [
      { path: "/auditForms", label: "Audit Forms", icon: "fa fa-wpforms" },
      { path: "/audits", label: "Audits", icon: "Audits.png" },
      { path: "/deleted-audits", label: "Deleted Audits", icon: "Audits.png" },
      {
        path: "",
        label: "Escalations",
        icon: "escalations.png",
        subLinks: [
          {
            path: "/escalations/internal-error-rca",
            label: "Internal Error RCA",
            icon: "escalations.png",
          },
        ],
      },
    ],
    qc: [
      { path: "/departments", label: "Departments", icon: "fa fa-building-o" },
      { path: "/processes", label: "Processes", icon: "fa fa-refresh" },
      { path: "/auditForms", label: "Audit Forms", icon: "fa fa-wpforms" },
      { path: "/audits", label: "Audits", icon: "Audits.png" },
      { path: "/deleted-audits", label: "Deleted Audits", icon: "Audits.png" },
      // { path: "/escalations", label: "Escalations", icon: "escalations.png" },
      {
        path: "",
        label: "Escalations",
        icon: "escalations.png",
        subLinks: [
          {
            path: "/escalations/client-escalation",
            label: "Client Escalation",
            icon: "escalations.png",
          },
          {
            path: "/escalations/internal-error-rca",
            label: "Internal Error RCA",
            icon: "escalations.png",
          },
        ],
      },
    ],
    tl: [{ path: "/audits", label: "Audits", icon: "Audits.png" }],
  };

  const vocLinks = {
    admin: [
      {
        path: "",
        label: "Feedback Requests",
        icon: "fa fa-commenting-o",
        subLinks: [
          {
            path: "/feedback-requests/open",
            label: "Open",
            icon: "fa fa-commenting-o",
          },
          {
            path: "/feedback-requests/closed",
            label: "Closed",
            icon: "fa fa-commenting-o",
          },
          {
            path: "/feedback-requests/expired",
            label: "Expired",
            icon: "fa fa-commenting-o",
          },
        ],
      },
      { path: "/forms/add", label: "Add Form", icon: "fa fa-wpforms" },
      { path: "/forms", label: "Forms", icon: "fa fa-wpforms" },
    ],
    qc: [
      {
        path: "",
        label: "Feedback Requests",
        icon: "fa fa-commenting-o",
        subLinks: [
          {
            path: "/feedback-requests/open",
            label: "Open",
            icon: "fa fa-commenting-o",
          },
          {
            path: "/feedback-requests/closed",
            label: "Closed",
            icon: "fa fa-commenting-o",
          },
          {
            path: "/feedback-requests/expired",
            label: "Expired",
            icon: "fa fa-commenting-o",
          },
        ],
      },
      { path: "/forms", label: "Forms", icon: "fa fa-wpforms" },
    ],
  };

  const mpsruLinks = {
    admin: [
      { path: "/permissions", label: "Permissions", icon: "fa fa-wpforms" },
      { path: "/sections", label: "Sections", icon: "fa fa-wpforms" },
      { path: "/roles", label: "Roles", icon: "fa fa-wpforms" },
      { path: "/users", label: "Users", icon: "fa fa-users" },
    ],
  };

  const settingLinks = {
    admin: [
      {
        path: "/settings/theme-options",
        label: "Theme Options",
        icon: "fa fa-wpforms",
      },
      {
        path: "/settings/import-bulk-data",
        label: "Import Bulk Data",
        icon: "fa fa-wpforms",
      },
    ],
  };

  const renderSubLinks = (subLinks) =>
    subLinks.map(({ path, label }) => {
      const isActive = location.pathname.startsWith(path);

      return (
        <Link
          key={label}
          to={path}
          onClick={() => {
            handleSubMenuClick(null, path);

            // CRITICAL FIX
            if (path.startsWith("/escalations")) {
              setIsActive("qms");
              setIsEscalationsActive(true);
            }

            if (path.startsWith("/feedback-requests")) {
              setIsActive("voc");
              setIsFeedbackRequestsActive(true);
            }
          }}
          className={`links ${isActive ? "subMenu-active-item" : ""}`}
        >
          <span className="dashboardstyle">{label}</span>
        </Link>
      );
    });

  return (
    <div className="sidenav">
      <div
        className="logo-area"
        onClick={() => {
          navigate("/dashboard");
        }}
      >
        {globalState?.logo && (
          <img
            src={process.env.REACT_APP_BACKEND + `/images/${globalState?.logo}`}
            className="logo-img"
            alt="logo"
          />
        )}
        <span className="qcc_logo">{globalState?.logoTitle}</span>
      </div>
      <div className="menu">
        {/*Dashboard*/}
        <div className={isActive === "dashboard" ? "menuActive" : ""}>
          <span
            className="d-flex links"
            onClick={() => {
              handleClick("dashboard");
              navigate("/dashboard");
            }}
          >
            <span className="dboardLnkContnt link-content">
              <img src={media.dashboardIcon.svg} alt="icon" /> Dashboard
            </span>
            <i className={isActive === "dashboard" ? "" : ""} />
          </span>
        </div>

        {/*QMS*/}
        {qmsLinks?.[role] && (
          <div className={isActive === "qms" ? "menuActive" : ""}>
            <span className="d-flex links" onClick={() => handleClick("qms")}>
              <span className="link-content">
                <img src={media.qmsIcon.svg} alt="icon" /> QMS
              </span>
              <i
                className={
                  isActive === "qms"
                    ? "fa fa-angle-up dropdown"
                    : "fa fa-angle-down dropdown"
                }
              />
            </span>
            <div
              className={
                isActive === "qms" ? "activeClass" : "inactiveClass dropwdown"
              }
            >
              {qmsLinks?.[role]?.map(({ path, label, icon, subLinks }) => (
                <React.Fragment key={label}>
                  {label === "Escalations" ? (
                    <span
                      className={`links subMnu escalation ${
                        isEscalationsActive ? "subMnuActive" : ""
                      }`}
                      onClick={handleClientEscalationClick}
                    >
                      <span className="dashboardstyle">
                        {label}
                        <i
                          className={
                            base === "escalations" || isEscalationsActive
                              ? "fa fa-angle-up dropdown"
                              : "fa fa-angle-down dropdown"
                          }
                        />
                      </span>
                    </span>
                  ) : (
                    <Link
                      to={path}
                      className={`links ${
                        base === path.replace("/", "")
                          ? "subMenu-active-item"
                          : ""
                      }`}
                      onClick={() => handleSubMenuClick("qms", path)}
                    >
                      <span className="dashboardstyle">{label}</span>
                    </Link>
                  )}
                  {subLinks && (
                    <div
                      className={
                        base === "escalations" || isEscalationsActive
                          ? "activeClass subMnuChildActive escalation"
                          : "inactiveClass dropwdown escalation"
                      }
                    >
                      {renderSubLinks(subLinks)}{" "}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        {role === "admin" || role === "qc" ? (
          <div className={isActive === "voc" ? "menuActive" : ""}>
            {/* VOC MAIN PARENT */}
            <span className="d-flex links" onClick={() => handleClick("voc")}>
              <span className="dboardLnkContnt link-content">
                <img src={media.voc.svg} alt="icon" /> VOC
              </span>

              <i
                className={
                  isActive === "voc"
                    ? "fa fa-angle-up dropdown"
                    : "fa fa-angle-down dropdown"
                }
              />
            </span>
            {/* VOC SUBMENU WRAPPER */}
            <div
              className={
                isActive === "voc" ? "activeClass" : "inactiveClass dropwdown"
              }
            >
              {vocLinks?.[role]?.map(({ path, label, icon, subLinks }) => (
                <React.Fragment key={label}>
                  {label === "Feedback Requests" ? (
                    <span
                      className={`links subMnu ${
                        isFeedbackRequestsActive ? "subMnuActive" : ""
                      }`}
                      onClick={() =>
                        setIsFeedbackRequestsActive((prev) => !prev)
                      }
                    >
                      <span className="dashboardstyle">
                        {label}
                        <i
                          className={`fa dropdown ${
                            isFeedbackRequestsActive
                              ? "fa-angle-up"
                              : "fa-angle-down"
                          }`}
                        />
                      </span>
                    </span>
                  ) : (
                    /* NORMAL VOC LINKS */
                    <Link
                      key={label}
                      to={path}
                      className={`links ${
                        location.pathname === path ? "subMenu-active-item" : ""
                      }`}
                      onClick={() => handleSubMenuClick("voc", path)}
                    >
                      <span className="dashboardstyle">{label}</span>
                      {subLinks && (
                        <i
                          className={`fa dropdown ${
                            isFeedbackRequestsActive
                              ? "fa-angle-up"
                              : "fa-angle-down"
                          }`}
                          style={{ float: "right" }}
                        />
                      )}
                    </Link>
                  )}
                  {subLinks && (
                    <>
                      <div
                        className={
                          isFeedbackRequestsActive
                            ? "activeClass subMnuChildActive"
                            : "inactiveClass dropwdown"
                        }
                      >
                        {renderSubLinks(subLinks)}
                      </div>
                    </>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        ) : null}
        {role === "admin" ? (
          <div className={isActive === "mpsru" ? "menuActive" : ""}>
            <span className="d-flex links" onClick={() => handleClick("mpsru")}>
              <span className="dboardLnkContnt link-content">
                <img src={media.manageUsers.svg} alt="icon" /> Manage Users
              </span>

              <i
                className={
                  isActive === "mpsru"
                    ? "fa fa-angle-up dropdown"
                    : "fa fa-angle-down dropdown"
                }
              />
            </span>
            <div
              className={
                isActive === "mpsru" ? "activeClass" : "inactiveClass dropwdown"
              }
            >
              {mpsruLinks[role].map(({ path, label, icon, subLinks }) => (
                <Link
                  key={label}
                  to={path}
                  className={`links ${
                    location.pathname === path ? "subMenu-active-item" : ""
                  }`}
                  onClick={() => handleSubMenuClick("mpsru", path)}
                >
                  {/* <i className={icon} aria-hidden="true"></i> */}
                  <span className="dashboardstyle">{label}</span>
                </Link>
              ))}
            </div>
          </div>
        ) : null}

        {role === "admin" && (
          <>
            <div className={isActive === "settings" ? "menuActive" : ""}>
              <span
                className="d-flex links"
                onClick={() => handleClick("settings")}
              >
                <span className="link-content">
                  <img src={media.settingsIcon.svg} alt="icon" /> Settings
                </span>

                <i
                  className={
                    isActive === "settings"
                      ? "fa fa-angle-up dropdown"
                      : "fa fa-angle-down dropdown"
                  }
                />
              </span>
              <div
                className={
                  isActive === "settings"
                    ? "activeClass"
                    : "inactiveClass dropwdown"
                }
              >
                {settingLinks[role].map(({ path, label, icon, subLinks }) => (
                  <React.Fragment key={label}>
                    {label === "Feedback Requests" ? (
                      <span
                        key={label}
                        className={
                          (location.pathname.split("/")[1] ===
                            "feedback-requests" &&
                            location.pathname.split("/")[2]) ||
                          isFeedbackRequestsActive !== false
                            ? "links subMnu subMnuActive"
                            : "links subMnu"
                        }
                        onClick={() => {
                          label === "Feedback Requests"
                            ? handleFeedbackRequestsClick("freq")
                            : handleSubMenuClick("settings", path);
                        }}
                      >
                        {label}

                        <i
                          className={
                            (location.pathname.split("/")[1] ===
                              "feedback-requests" &&
                              location.pathname.split("/")[2]) ||
                            isFeedbackRequestsActive !== false
                              ? "fa fa-angle-up dropdown"
                              : "fa fa-angle-down dropdown"
                          }
                        />
                      </span>
                    ) : (
                      <Link
                        key={label}
                        to={path}
                        className={`links ${
                          location.pathname === path
                            ? "subMenu-active-item"
                            : ""
                        }`}
                        onClick={() => handleSubMenuClick("settings", path)}
                      >
                        <span className="dashboardstyle">{label}</span>
                        {subLinks && (
                          <i
                            className={
                              (location.pathname.split("/")[1] ===
                                "feedback-requests" &&
                                location.pathname.split("/")[2]) ||
                              isFeedbackRequestsActive !== false
                                ? "fa fa-angle-up dropdown"
                                : "fa fa-angle-down dropdown"
                            }
                            style={{ float: "right" }}
                          />
                        )}
                      </Link>
                    )}
                    {subLinks && (
                      <div
                        className={
                          (location.pathname.split("/")[1] ===
                            "feedback-requests" &&
                            location.pathname.split("/")[2]) ||
                          isFeedbackRequestsActive !== false
                            ? "activeClass subMnuChildActive"
                            : "inactiveClass dropwdown"
                        }
                      >
                        {renderSubLinks(subLinks)}{" "}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </>
        )}
        {secureLocalStorage.getItem("empID") ===
          Number(process.env.REACT_APP_SROLE_ID) && (
          <div className={isActive === "switch-role" ? "menuActive" : ""}>
            <Link
              to="/switch-role"
              className="d-flex links"
              onClick={() => {
                handleClick("switch-role");
                handleSubMenuClick("switch-role");
              }}
            >
              <span className="dashboardstyle settings">Switch Role</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default NavBar;
